import { isoDate } from "@core/foundation/date";
import { ulid } from "@core/foundation/id";
import {
  createModel,
  createModelId,
  createModelSchemas,
} from "@core/foundation/model";
import { z } from "@core/zod";

import { PrincipalIdSchema } from "../../identity/index.ts";
import { ID_PREFIX } from "../../shared/id-prefix.ts";

const JobStatusSchema = z.enum([
  "completed",
  "failed",
  "pending",
  "processing",
]);

export type JobStatus = z.infer<typeof JobStatusSchema>;

const { generateId: generateJobId, idSchema: JobIdSchema } = createModelId({
  digestFn: ulid,
  idPrefix: ID_PREFIX.JOB,
});

export type JobId = z.infer<typeof JobIdSchema>;

export const Job = createModel({
  create: (item, schema) => {
    const now = isoDate();

    return { ...schema.decode(item), id: generateJobId(), updatedAt: now };
  },
  schemas: createModelSchemas({
    defaults: { iteration: 1 },
    model: z.object(
      {
        error: z.string().optional(),
        id: JobIdSchema,
        iteration: z.int().positive(),
        modificationKeyHash: z.string(),
        notificationChannels: z.array(z.safeId()).optional(),
        principalId: PrincipalIdSchema,
        resourceId: z.safeId(),
        status: JobStatusSchema,
        type: z.safeId(),
        updatedAt: z.iso.datetime(),
      },
      {
        error: "The error message the job failed with.",
        id: "The unique identifier for the job.",
        iteration: "The job's iteration number, starting at 1.",
        modificationKeyHash:
          "The digest of the key a caller presents to update the job.",
        notificationChannels:
          "The channels that receive the job's status changes.",
        principalId:
          "The id of the user the job serves, or of the service that runs it.",
        resourceId: "The id of the resource the job works on.",
        status: "The job's status: pending, processing, completed, or failed.",
        type: "The job's type, naming the work it does.",
        updatedAt: "When the job last changed, as an ISO 8601 timestamp.",
      },
    ),
    modelCreate: ({ readSchema }) =>
      readSchema.omit({ error: true, id: true, updatedAt: true }),
    modelUpdate: ({ schema }) =>
      schema.pick({ error: true, iteration: true, status: true }).partial(),
  }),
  update: (item, update, schema) => {
    return { ...item, ...schema.decode(update) };
  },
});

export interface Job extends z.infer<typeof Job.schema> {}
